// ============================================
// Patient Login Page
//
// Full-page login/register for returning patients.
// Route: /login
// Design system: light theme, Instrument Serif titles,
// DM Sans body, Rose accent, WCAG AA.
// No framer-motion.
// ============================================

import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PatientAuthForm } from './PatientAuthForm';
import { findActiveJourneyByUid } from '../../services/firestore/journeyResumeService';

export function PatientLogin() {
  const navigate = useNavigate();

  async function handleSuccess(uid: string, _email: string) {
    // Check for active journey to resume
    try {
      const active = await findActiveJourneyByUid(uid);
      if (active) {
        // Set sessionId so ScreenRouter can hydrate
        try {
          localStorage.setItem('hemologix_session_id', active.journey.sessionId);
        } catch { /* localStorage unavailable */ }
        navigate('/app');
        return;
      }
    } catch (err) {
      console.warn('[PatientLogin] Active journey check failed:', err);
    }
    navigate('/my-results');
  }

  function handleBack() {
    navigate('/');
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Top bar */}
      <div className="sticky top-0 z-10 bg-white border-b border-[#E4E4E7] px-4 py-3">
        <div className="flex items-center gap-3 max-w-md mx-auto">
          <button
            onClick={handleBack}
            className="text-[#A1A1AA] hover:text-[#18181B] transition-colors"
            aria-label="Back to home"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1
              className="text-lg text-[#18181B]"
              style={{ fontFamily: "'Instrument Serif', serif" }}
            >
              HemoLogix
            </h1>
            <p className="text-[#A1A1AA] text-[13px]">Patient Account</p>
          </div>
        </div>
      </div>

      {/* Auth form */}
      <div className="px-4 py-8 max-w-md mx-auto">
        <PatientAuthForm
          initialMode="login"
          onSuccess={handleSuccess}
        />
      </div>

      {/* Footer */}
      <div className="text-center pb-8">
        <p className="text-[12px] text-[#A1A1AA]">
          HemoLogix Health LLC
        </p>
      </div>
    </div>
  );
}
